import React, { useState, useEffect } from 'react';
import { Layout, Typography, Form, Input, Card, Divider, Select, Avatar, Row, Col, Button } from 'antd';
import { UserOutlined, MailOutlined, MenuOutlined, FileTextOutlined, LogoutOutlined } from '@ant-design/icons';
import ScholarSidebar from '../components/ScholarSidebar';
import '../styles/ScholarProfileManagement.css';
import '../styles/SharedLayout.css';
import '../styles/MobileMenuShared.css';
import { useHistory } from 'react-router-dom';

const { Content } = Layout;
const { Title, Text } = Typography;

/**
 * ScholarProfileManagement Component
 * 
 * Provides interface for scholars to view their profile information
 * using data stored in localStorage from login
 */
const ScholarProfileManagement = () => {
    // Form and state hooks
    const [form] = Form.useForm();
    const history = useHistory();
    
    // State for mobile sidebar visibility
    const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
    
    // Toggle mobile sidebar
    const toggleMobileSidebarVisibility = () => {
        setMobileSidebarVisible(!mobileSidebarVisible);
    };
    
    // Handle mobile menu item clicks
    const handleMobileMenuClick = (path) => {
        setMobileSidebarVisible(false);
        history.push(path);
    };
    
    // Mobile menu items
    const mobileMenuItems = [
        {
            key: 'acknowledgement-receipt',
            icon: <FileTextOutlined />,
            label: 'AR',
            onClick: () => handleMobileMenuClick('/scholar-acknowledgement-receipt')
        },
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: 'Profile',
            onClick: () => handleMobileMenuClick('/scholar-profile')
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Logout',
            onClick: () => handleMobileMenuClick('/scholar-logout')
        }
    ];
    
    const [userData, setUserData] = useState({
        firstName: '',
        middleName: '',
        lastName: '',
        suffix: '',
        email: '',
        course: '',
        gender: '',
        scholarType: ''
    });

    // Load profile data from localStorage on component mount
    useEffect(() => {
        let isMounted = true;

        // Get all user data from localStorage
        const first_name = localStorage.getItem('first_name') || '';
        const middle_name = localStorage.getItem('middle_name') || '';
        const last_name = localStorage.getItem('last_name') || '';
        const suffix = localStorage.getItem('suffix') || '';
        const email = localStorage.getItem('email') || '';
        const gender = localStorage.getItem('gender') || '';
        const course = localStorage.getItem('course') || '';
        const scholarType = localStorage.getItem('scholarType') || 'College';

        // Set user data from localStorage
        const userProfileData = {
            firstName: first_name,
            middleName: middle_name,
            lastName: last_name,
            suffix: suffix,
            email: email,
            course: course || 'Not specified',
            gender: gender || 'Not specified',
            scholarType: scholarType
        };

        // Only update state if component is still mounted
        if (isMounted) {
            setUserData(userProfileData);
            form.setFieldsValue(userProfileData);
        }

        // Cleanup function that runs when component unmounts
        return () => {
            isMounted = false;
        };
    }, [form]);

    return (
        <div className="page-wrapper">
            <Layout className="page-body">
                {/* Mobile Toggle Button and Menu */}
                <div className="mobile-menu-container">
                    <Button
                        type="primary"
                        className="mobile-sidebar-toggle"
                        icon={<MenuOutlined />}
                        onClick={toggleMobileSidebarVisibility}
                        aria-label="Toggle navigation menu"
                        shape="circle"
                        size="large"
                    />
                    
                    {/* Mobile Menu Popup */}
                    <div className={`mobile-menu-popup ${mobileSidebarVisible ? 'visible' : ''}`}>
                        {mobileMenuItems.map(item => (
                            <Button 
                                key={item.key}
                                className={`mobile-menu-item ${item.key === 'profile' ? 'active' : ''}`}
                                icon={item.icon}
                                onClick={item.onClick}
                                type="text"
                                block
                            >
                                {item.label}
                            </Button>
                        ))}
                    </div>
                </div>
                
                {/* Mobile Menu Overlay */}
                <div 
                    className={`mobile-menu-overlay ${mobileSidebarVisible ? 'visible' : ''}`}
                    onClick={() => setMobileSidebarVisible(false)}
                />
                
                {/* Regular Sidebar (only visible on desktop) */}
                <ScholarSidebar activePage="profile" />
                <Content className="main-content">
                    <Card 
                        className="spm-profile-card"
                        style={{ maxWidth: '800px', margin: '0 auto' }}
                    >
                        <div className="spm-profile-header" style={{ justifyContent: 'center', textAlign: 'center' }}>
                            <div className="spm-avatar-section">
                                <Avatar 
                                    size={100} 
                                    icon={<UserOutlined />} 
                                    className="spm-avatar" 
                                />
                            </div>
                            
                            <div className="spm-profile-summary">
                                <Title level={4}>
                                    {userData.firstName} {userData.middleName} {userData.lastName} {userData.suffix}
                                </Title>
                                <Text>{userData.course}</Text>
                                <Text type="secondary">Scholar</Text>
                            </div>
                        </div>
                        
                        <Divider />
                        
                        <Form
                            form={form}
                            layout="vertical"
                            className="spm-profile-form"
                            style={{ maxWidth: '700px', margin: '0 auto' }}
                        >
                            <div className="spm-form-section">
                                <Title level={5}>Personal Information</Title>
                                
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="firstName"
                                            label="First Name"
                                        >
                                            <Input prefix={<UserOutlined />} disabled />
                                        </Form.Item>
                                    </Col>
                                    
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="middleName"
                                            label="Middle Name"
                                        >
                                            <Input prefix={<UserOutlined />} disabled />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="lastName"
                                            label="Last Name"
                                        >
                                            <Input prefix={<UserOutlined />} disabled />
                                        </Form.Item>
                                    </Col>
                                    
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="suffix"
                                            label="Suffix"
                                        >
                                            <Input disabled />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="email"
                                            label="Email Address"
                                        >
                                            <Input 
                                                prefix={<MailOutlined />}
                                                disabled
                                            />
                                        </Form.Item>
                                    </Col>
                                    
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="gender"
                                            label="Gender"
                                        >
                                            <Select disabled style={{ width: '100%' }}>
                                                <Select.Option value="Male">Male</Select.Option>
                                                <Select.Option value="Female">Female</Select.Option>
                                                <Select.Option value="Not specified">Not specified</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                            
                            <div className="spm-form-section">
                                <Title level={5}>Academic Information</Title>
                                
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="course"
                                            label="Course/Program"
                                        >
                                            <Input disabled />
                                        </Form.Item>
                                    </Col>
                                    
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="scholarType"
                                            label="Scholar Type"
                                        >
                                            <Select disabled style={{ width: '100%' }}>
                                                <Select.Option value="College">College</Select.Option>
                                                <Select.Option value="High School">High School</Select.Option>
                                                <Select.Option value="Not specified">Not specified</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                        </Form>
                    </Card>
                    
                    {/* Copyright Section */}
                    <div className="spm-company-branding">
                        <Text className="spm-company-text">© {new Date().getFullYear()} MYT Softdev Solutions Inc.</Text>
                    </div>
                </Content>
            </Layout>
        </div>
    );
};

export default ScholarProfileManagement; 